import React from 'react'
import { isEmpty } from 'lodash'
import { getHours, getMinutes, parseISO, set } from 'date-fns'
import {
  getAddressGqlCompliant,
  TestSessionWithoutMembersWithResult_testSession,
  TestSessionFieldName,
  useEditTestSession,
  useTestSessionForm,
} from '@modmd/data'
import { pxToRem } from 'theme'
import { Dialog } from 'components/Dialog'
import Card from 'components/Card'
import { AddressInput } from 'components/AddressInput'
import Button from 'components/Button'
import { DateTime } from 'components/DateTime'

interface Props {
  session: TestSessionWithoutMembersWithResult_testSession
  isEditing: boolean
  onCloseDialog: () => void
  onSuccess?: () => void
}

export const EditSession: React.VFC<Props> = ({ session, isEditing, onCloseDialog, onSuccess }) => {
  const [editSession, { loading: isEditSessionLoading }] = useEditTestSession({})

  const {
    values,
    setFieldValue,
    errors,
    touched,
    setTouched,
    handleSubmit,
    handleBlur,
  } = useTestSessionForm({
    onSubmit: async () => {
      try {
        const response = await editSession({
          variables: {
            inputData: {
              id: session.id,
              date: values.date,
              ...(session.endsAt ? { endsAt: values.endDate } : {}),
              Location: {
                id: session.Location.id,
                ...getAddressGqlCompliant(values.Address),
              },
              type: session.type,
              isScheduled: session.isScheduled,
            },
          },
        })
        if (response.data?.editTestSession?.id) {
          if (onSuccess) {
            onSuccess()
          }
        }
      } catch {
        // ignore
      }
    },
  })

  React.useEffect(() => {
    void setFieldValue(TestSessionFieldName.DATE, session.date ? parseISO(session.date) : undefined)
    void setFieldValue(
      TestSessionFieldName.END_DATE,
      session.endsAt ? parseISO(session.endsAt) : undefined
    )
    void setFieldValue(TestSessionFieldName.ADDRESS, session.Location)
  }, [session.Location, session.date, session.endsAt, setFieldValue])

  return (
    <Dialog maxWidth={pxToRem(400)} isOpen={isEditing} onDismiss={onCloseDialog}>
      <Card.Title onDismiss={onCloseDialog}>Edit Test Session</Card.Title>
      <Card.Content>
        <DateTime
          isColumnOrientation
          hasTime
          hasEndTime={!!session.endsAt}
          dateValue={values.date || null}
          timeValue={values.date || null}
          endTimeValue={values.endDate || null}
          onDateChange={(date) => {
            void setFieldValue(TestSessionFieldName.END_DATE, null)
            void setFieldValue(TestSessionFieldName.DATE, date)
          }}
          onTimeChange={(date) => {
            void setFieldValue(TestSessionFieldName.END_DATE, null)
            void setFieldValue(TestSessionFieldName.DATE, date)
          }}
          onEndTimeChange={(date) => {
            if (values.date) {
              void setFieldValue(
                TestSessionFieldName.END_DATE,
                date
                  ? set(values.date, {
                      hours: getHours(date),
                      minutes: getMinutes(date),
                    })
                  : null
              )
            }
          }}
          onDateBlur={handleBlur}
          onTimeBlur={handleBlur}
          errorMessageDate={(touched.date && errors.date) || undefined}
        />
        <AddressInput
          onTouched={(touched) => setTouched({ ...touched, Address: touched })}
          touched={touched.Address}
          errors={errors.Address}
          value={values.Address}
          onChange={(address) => {
            void setFieldValue(TestSessionFieldName.ADDRESS, address)
          }}
        />
      </Card.Content>
      <Card.Actions>
        <Button colorVariant="secondary" onClick={onCloseDialog}>
          Cancel
        </Button>
        <Button
          disabled={(session.endsAt && !values.endDate) || !values.date || !isEmpty(errors.Address)}
          isFetching={isEditSessionLoading}
          colorVariant="success"
          onClick={() => handleSubmit()}
        >
          Save
        </Button>
      </Card.Actions>
    </Dialog>
  )
}
